import { Directive, ElementRef, NgZone, OnDestroy, inject, OnInit } from "@angular/core";
import { DropDownListComponent, MultiSelectComponent, ComboBoxComponent } from "@progress/kendo-angular-dropdowns";
import { Subject } from "rxjs";

interface DropDownLike {
    toggle(open: boolean): void;
    name?: string;
    scrolled?: boolean;
    opened: { subscribe: (fn: () => void) => any };
    closed: { subscribe: (fn: () => void) => any };
}

@Directive({
    selector: "[closeOnAncestorScroll]",
})
export class CloseOnAncestorScrollDirective implements OnInit, OnDestroy {
    private readonly hostEl = inject(ElementRef<HTMLElement>);
    private readonly zone = inject(NgZone);

    private readonly asDDL = inject(DropDownListComponent, { self: true, optional: true });
    private readonly asCombo = inject(ComboBoxComponent, { self: true, optional: true });
    private readonly asMulti = inject(MultiSelectComponent, { self: true, optional: true });

    private readonly ddl: DropDownLike | null = (this.asDDL ?? this.asCombo ?? this.asMulti) as unknown as DropDownLike | null;

    private intersectionObserver?: IntersectionObserver;
    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.ddl?.opened?.subscribe(() => this.attach());
        this.ddl?.closed?.subscribe(() => this.detach());
    }

    private attach() {
        this.setupIntersectionObserver();
    }

    private detach() {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;
    }

    ngOnDestroy() {
        this.detach();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupIntersectionObserver() {
        this.zone.runOutsideAngular(() => {
            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => this.closeIfOutsideViewport(entry));
                },
                {
                    root: null,
                    threshold: 0,
                },
            );

            this.intersectionObserver.observe(this.hostEl.nativeElement);
        });
    }

    private closeIfOutsideViewport(entry: IntersectionObserverEntry) {
        if (!entry.intersectionRatio) {
            this.zone.run(() => {
                const isMulti = !!this.asMulti;
                if (isMulti) this.ddl!.scrolled = true;
                this.ddl!.toggle(false);
            });
        }
    }
}
